import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'ramda'
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks'
import { useHistory, useLocation } from 'react-router-dom'
import {
  DesktopTable,
  MobileTable,
  AddIcon as AddCreditCardIcon,
  getTableColumns,
  LoadingTable
} from './payment-table-components'
import styles from './payment.css'
import {
  checkCardLimit,
  checkTableData,
  getCreditCardAddResponse,
  getCreditCardUpdateResponse,
  getProductType,
  handleTableDataForIframes
} from './my-saved-cards-table-helpers'

import ConfirmationModal from '../../../shared/confirmation-modal'
import { CardInnerHeading } from '../../../shared/settings/card'
import CREDIT_CARD_FRAGMENT from '../../../../../graphql/fragments/credit-cards.gql'
import ACCOUNT_CREDIT_CARDS_QUERY from '../../../../../graphql/queries/account-credit-cards.gql'
import CREDIT_CARD_ADD_QUERY from '../../../../../graphql/queries/creditcard-add.gql'
import CREDIT_CARD_UPDATE_QUERY from '../../../../../graphql/queries/creditcard-update.gql'
import DELETE_CREDITCARD_MUTATION from '../../../../../graphql/mutations/delete-creditcard.gql'
import REMOVE_CREDITCARD_DEFAULT_MUTATION from '../../../../../graphql/mutations/remove-creditcard-default.gql'

import {
  MODALS,
  SCREEN_SIZE_BREAKPOINTS,
  SUBSCRIPTION_STATUS
} from '../../../../constants'
import { useScreenWidth } from '../../../../hooks/use-screen-width'
import { checkQueryStatus } from '../../../../lib/utils'
import { getGQLErrorMsg } from '../../../../lib/apollo'
import { getModalLocation } from '../../../../lib/modal'
import { useMutateAndLoading } from '../../../../hooks/use-mutate-and-loading'
import { useSetCreditcardDefaultMutation } from '../../../../hooks/use-set-creditcard-default-mutation'

const updateRemoveDefaultCreditCardCache = (proxy, paymentMethodId, productType) => {
  const accountCache = proxy.readQuery({
    query: ACCOUNT_CREDIT_CARDS_QUERY
  })

  const currentCreditcards = accountCache?.account?.creditcards || []
  // Removing a default card only turns off the default setting for that card
  const productTypeField = {
    TVOD: 'useForPurchases',
    SVOD: 'useForSubscriptions'
  }[productType]

  const updatedCreditcards = currentCreditcards.map((card) => {
    if (card.id === paymentMethodId) {
      return {
        ...card,
        [productTypeField]: false
      }
    }

    return card
  })

  updatedCreditcards.forEach((creditcard) => {
    proxy.writeFragment({
      id: `Creditcard:${creditcard.id}`,
      fragment: CREDIT_CARD_FRAGMENT,
      data: creditcard
    })
  })
}

function MySavedCardsTable({
  activeSubscription
}) {
  const client = useApolloClient()
  const history = useHistory()
  const location = useLocation()
  const screenWidth = useScreenWidth()
  const isDesktopView = screenWidth >= SCREEN_SIZE_BREAKPOINTS.MIN_480
  const accountCreditCardsQuery = useQuery(ACCOUNT_CREDIT_CARDS_QUERY, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  })
  const [deleteCreditCardMutation] = useMutation(DELETE_CREDITCARD_MUTATION)
  const [removeCreditcardDefaultMutation] = useMutation(REMOVE_CREDITCARD_DEFAULT_MUTATION)
  const deleteCreditCardWithLoading = useMutateAndLoading(deleteCreditCardMutation)
  const removeCreditcardDefaultWithLoading = useMutateAndLoading(removeCreditcardDefaultMutation)
  const setCreditcardDefault = useSetCreditcardDefaultMutation()
  const accountCreditCards = accountCreditCardsQuery.data?.account?.creditcards || []

  const [error, setError] = useState('')
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [paymentMethodId, setPaymentMethodId] = useState('')
  const [iframeIsOpen, setIframeIsOpen] = useState(false)
  const [iframeEditUrl, setIframeEditUrl] = useState('')
  const [iframeAddUrl, setIframeAddUrl] = useState('')
  const [cardRowIndex, setCardRowIndex] = useState(0)
  const [activeEditionPanel, setActiveEditionPanel] = useState(null)
  const [isAddNewCreditCard, setIsAddNewCreditCard] = useState(false)
  const [tableData, setTableData] = useState(accountCreditCards)

  // Keep the table data in sync after credit cards are refetched or updated in Apollo cache
  useEffect(() => {
    if (checkTableData(tableData, accountCreditCards)) {
      setTableData(accountCreditCards)
    }
  }, [accountCreditCards, tableData])

  const hasReachedLimit = checkCardLimit(tableData)

  const handleCreditCardPanelChange = () => {
    setActiveEditionPanel(null)
    setIsAddNewCreditCard(false)
    setIframeIsOpen(false)
  }

  const setDisplayConfirmationModal = (creditCardID) => {
    setIsConfirmationModalOpen(Boolean(creditCardID))
    setPaymentMethodId(creditCardID || '')
  }

  const setCreditCardUpdateDetails = ({ paymentMethodId: methodId, iframe }) => {
    setPaymentMethodId(methodId)
    setIframeEditUrl(iframe)
  }

  const setDisplayCreditCardIframe = (
    creditCardID,
    nextCardRowIndex,
    isAddNew
  ) => {
    setIframeIsOpen(Boolean(creditCardID))
    setActiveEditionPanel(isAddNew ? null : creditCardID)
    setIsAddNewCreditCard(Boolean(isAddNew))
    setCardRowIndex(nextCardRowIndex || 0)
    // Desktop add-card flow needs an empty row so the Evergent iframe can be
    // rendered inside the table
    setTableData(isDesktopView
      ? handleTableDataForIframes(tableData, isAddNew)
      : tableData)
  }

  const prefetchCreditCardAddResponse = () => {
    if (iframeIsOpen || hasReachedLimit) return

    client.query({
      query: CREDIT_CARD_ADD_QUERY
    }).then(response => {
      setIframeAddUrl(getCreditCardAddResponse(response))
    })
  }

  useEffect(() => {
    // Mobile opens the add-card iframe from an accordion panel, so prefetch the iframe URL early
    if (!isDesktopView) {
      prefetchCreditCardAddResponse()
    }
  }, [])

  const handleRepairSubscription = () => {
    history.push(getModalLocation(location, MODALS.qsParams.updatePaymentDetails))
  }

  const prefetchCreditCardUpdateResponse = (creditCardID) => {
    if (iframeIsOpen || creditCardID === paymentMethodId) return

    client.query({
      query: CREDIT_CARD_UPDATE_QUERY,
      variables: { id: creditCardID }
    }).then(response => {
      setCreditCardUpdateDetails(getCreditCardUpdateResponse(response))
    })
  }

  const handleMobilePanelChange = (creditCardID) => {
    handleCreditCardPanelChange()

    if (
      creditCardID &&
      (!iframeEditUrl || creditCardID !== paymentMethodId)
    ) {
      prefetchCreditCardUpdateResponse(creditCardID)
    }
  }

  const deleteCreditCard = id => (
    deleteCreditCardWithLoading('creditCardDelete', {
      variables: { id },
      update: (proxy, result) => {
        const data = proxy.readQuery({
          query: ACCOUNT_CREDIT_CARDS_QUERY
        })

        const currentCreditcards = data?.account?.creditcards || []
        const creditCardDeletedSuccessfully = result?.data?.deleteCreditcard

        if (creditCardDeletedSuccessfully) {
          const creditcards = currentCreditcards.filter(card => card.id !== id)

          proxy.writeQuery({
            query: ACCOUNT_CREDIT_CARDS_QUERY,
            data: {
              account: {
                ...data.account,
                creditcards: [...creditcards]
              }
            }
          })
        }
      }
    })
  )

  const handleDeleteCardModalCancelClick = () => {
    setDisplayConfirmationModal(false)
  }

  const handleDeleteCardModalConfirmClick = () => {
    deleteCreditCard(paymentMethodId).then(() => {
      setDisplayConfirmationModal(false)
    })
  }

  const handleRemoveCreditCard = (creditCardID) => {
    setDisplayConfirmationModal(creditCardID)
  }

  const handleAddCreditCard = () => {
    if (iframeIsOpen || !iframeAddUrl || hasReachedLimit) return

    if (activeSubscription?.status === SUBSCRIPTION_STATUS.SUSPENDED) {
      handleRepairSubscription()
      return
    }

    setDisplayCreditCardIframe(true, 0, true)
  }

  const handleEditCreditCard = (creditCardID, nextCardRowIndex) => {
    if (iframeIsOpen || !iframeEditUrl) return

    setDisplayCreditCardIframe(creditCardID, nextCardRowIndex, false)
  }

  const handleCheckboxDefaultCard = ({
    column,
    original,
    columnName
  }) => {
    setError('')
    const nextPaymentMethodId = original.id
    const productType = getProductType({ column, columnName })

    setCreditcardDefault(nextPaymentMethodId, productType)
  }

  const removeCheckboxDefaultCard = ({
    column,
    original,
    columnName
  }) => {
    setError('')
    const nextPaymentMethodId = original.id
    const productType = getProductType({ column, columnName })

    removeCreditcardDefaultWithLoading('removeDefaultCreditcard', {
      variables: {
        id: nextPaymentMethodId,
        type: productType
      },
      update: (proxy, result) => {
        const paymentMethod = result?.data?.removeCreditcardDefault
        if (!paymentMethod) return

        updateRemoveDefaultCreditCardCache(proxy, nextPaymentMethodId, productType)
      }
    }).catch(mutationError => {
      setError(getGQLErrorMsg(mutationError))
    })
  }

  const onIframeLoad = (searchParams) => {
    if (isEmpty(searchParams)) return

    // Evergent redirects the iframe back with status=success after add/edit completes
    if (searchParams.status === 'success') {
      setDisplayCreditCardIframe()
      accountCreditCardsQuery.refetch()
    }
  }

  const handleIframeCancelClick = () => {
    setDisplayCreditCardIframe(null, null, false)
  }

  const tableColumns = getTableColumns(isDesktopView, {
    handleRemoveCreditCard,
    handleEditCreditCard,
    handleCheckboxDefaultCard,
    removeCheckboxDefaultCard
  })

  if (checkQueryStatus(accountCreditCardsQuery)) {
    return <LoadingTable />
  }

  return (
    <>
      <CardInnerHeading
        className={styles.cardInnerHeading}
        header="My saved cards"
        subHeader="Your default credit card will be used for monthly
          payments and movie rentals you authorise with your password or PIN."
      />
      <AddCreditCardIcon
        onMouseOver={prefetchCreditCardAddResponse}
        onClick={handleAddCreditCard}
        isDisabled={hasReachedLimit}
        id="AddCreditCardIcon"
      />
      {isDesktopView ? (
        <>
          <DesktopTable
            className={styles.mySavedCardsTable}
            tableData={tableData}
            tableColumns={tableColumns}
            onIframeLoad={onIframeLoad}
            iframeIsOpen={iframeIsOpen}
            iframeAddUrl={iframeAddUrl}
            iframeEditUrl={iframeEditUrl}
            cardRowIndex={cardRowIndex}
            handleIframeCancelClick={handleIframeCancelClick}
            prefetchCreditCardUpdateResponse={prefetchCreditCardUpdateResponse}
          />
          <span className={styles.errorMsg}>{error}</span>
        </>
      ) : (
        <>
          <MobileTable
            tableData={tableData}
            tableColumns={tableColumns}
            onIframeLoad={onIframeLoad}
            iframeIsOpen={iframeIsOpen}
            iframeAddUrl={iframeAddUrl}
            iframeEditUrl={iframeEditUrl}
            handleIframeCancelClick={handleIframeCancelClick}
            activeEditionPanel={activeEditionPanel}
            isAddNewCreditCard={isAddNewCreditCard}
            handleMobilePanelChange={handleMobilePanelChange}
          />
          <span className={styles.errorMsg}>{error}</span>
        </>
      )}
      {isConfirmationModalOpen && (
        <ConfirmationModal
          header="Remove this credit card?"
          message="Are you sure you want to remove this credit card from your account?"
          confirmText="Yes, Remove this card"
          cancelText="I'll keep this card"
          onCancelClick={handleDeleteCardModalCancelClick}
          onConfirmClick={handleDeleteCardModalConfirmClick}
          customWrapperClass={styles.removeCCardConfirmationWrapper}
        />
      )}
    </>
  )
}

MySavedCardsTable.propTypes = {
  activeSubscription: PropTypes.shape({
    status: PropTypes.string
  }).isRequired
}

export default MySavedCardsTable
